import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import ArgonButton from "components/ArgonButton";
import { getSubCategories } from "services/subCategoriesService";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Image } from "@mui/icons-material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DataTable from "examples/Tables/DataTable";
import { deleteSubCategory } from "services/subCategoriesService";
import SubCategoryModal from "./subCategoryModal";

function ViewSubCategoriesModal({ open, onClose, category, onSuccess }) {
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  useEffect(() => {
    const fetchSubCategories = async () => {
      if (!category?._id) return;
      setLoading(true);
      try {
        const response = await getSubCategories(category._id);
        setSubCategories(response.data || []);
      } catch (err) {
        console.error("Failed to fetch subcategories", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSubCategories();
  }, [category]);

  const handleDeleteSubCategory = async (id) => {
    try {
      await deleteSubCategory(id);
      setSubCategories(subCategories.filter((subCat) => subCat._id !== id));
      onSuccess();
    } catch (err) {
      console.error("Failed to delete sub-category", err);
    }
  };

  const handleEditSubCategory = (subCategory) => {
    setSelectedSubCategory(subCategory);
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setSelectedSubCategory(null);
  };

  const handleEditSuccess = () => {
    // Refresh the sub-categories list after successful edit
    const fetchSubCategories = async () => {
      if (!category?._id) return;
      setLoading(true);
      try {
        const response = await getSubCategories(category._id);
        setSubCategories(response.data || []);
      } catch (err) {
        console.error("Failed to fetch subcategories", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSubCategories();
    onSuccess();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        {loading ? (
          <ArgonBox display="flex" justifyContent="center" p={10}>
            <CircularProgress />
          </ArgonBox>
        ) : (
          <DialogContent
            sx={{
              bgcolor: "#fff",
              borderRadius: 2,
              p: 3,
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* Show a loading spinner on top of the modal when loading */}
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 10,
                }}
              >
                <CircularProgress size={50} />
              </Box>
            )}

            <ArgonTypography
              variant="h4"
              align="center"
              sx={{ fontWeight: "bold" }}
            >
              Sub-Category
            </ArgonTypography>

            <ArgonBox display="flex" alignItems="center">
              <Box
                style={{
                  width: 50,
                  height: 50,
                  marginRight: 10,
                  borderRadius: 15,
                  border: "1px solid #e0e0e0",
                  backgroundColor: "#f2f2f2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {category?.icon ? (
                  <img
                    src={category.icon}
                    alt="icon"
                    style={{
                      borderRadius: 5,
                      width: "60%",
                      height: "60%",
                    }}
                  />
                ) : (
                  <Image fontSize="large" color="primary" />
                )}
              </Box>
              <ArgonTypography fontWeight="medium" variant="h5">
                {category?.name}
              </ArgonTypography>
            </ArgonBox>

            <Box
              mt={2}
              style={{
                borderRadius: 10,
                boxShadow: "0 0 5px 1px rgba(18, 14, 14, 0.3)",
              }}
            >
              <DataTable
                table={{
                  columns: [
                    { Header: "Name", accessor: "name", align: "left" },
                    {
                      Header: "Actions",
                      accessor: "actions",
                      align: "center",
                    },
                  ],
                  rows: subCategories.map((subCat) => ({
                    name: (
                      <Typography
                        fontWeight="medium"
                        sx={{ fontSize: 16, color: "#172B4D" }}
                      >
                        {subCat.name}
                      </Typography>
                    ),
                    actions: (
                      <Box display="flex" justifyContent="center" gap={1}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteSubCategory(subCat._id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditSubCategory(subCat)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ),
                  })),
                }}
                entriesPerPage={false}
                canSearch={false}
                showTotalEntries={false}
                pagination={false}
                isLoading={loading}
              />
            </Box>

            <Box mt={3} textAlign="center">
              <ArgonButton
                variant="outlined"
                color="secondary"
                onClick={onClose}
                disabled={loading}
                sx={{
                  minWidth: 100,
                  backgroundColor: "#ffff",
                  color: "#2d3f50",
                  mt: 3,
                }}
              >
                Close
              </ArgonButton>
            </Box>
          </DialogContent>
        )}
      </Dialog>

      {/* Edit Sub-Category Modal */}
      {openEditModal && (
        <SubCategoryModal
          open={openEditModal}
          onClose={handleCloseEditModal}
          category={selectedSubCategory}
          onSuccess={handleEditSuccess}
        />
      )}
    </>
  );
}

export default ViewSubCategoriesModal;
